"use client";

import { FC, useCallback } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/components/shadcn/form";
import { Input } from "@/app/components/shadcn/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/shadcn/select";
import { PhoneInput } from "@/app/components/elements/forms/phoneInput/PhoneInput";
import { UseFormReturn, ControllerRenderProps } from "react-hook-form";
import { RegisterFormData } from "@/app/schemas/registerSchema";

interface RegisterInfoFormProps {
  form: UseFormReturn<RegisterFormData>;
  disabled?: boolean;
}

const RegisterInfoForm: FC<RegisterInfoFormProps> = ({ form, disabled }) => {
  const handleNameInput = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      field: ControllerRenderProps<RegisterFormData, "firstName" | "lastName">,
    ) => {
      const value = e.target.value;
      const filteredValue = value.replace(/[^a-zA-ZçğıöşüÇĞIİÖŞÜ\s]/g, "");
      e.target.value = filteredValue;
      field.onChange(e);
    },
    [],
  );

  const handleNameKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const char = e.key;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (
        [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
          "Tab",
        ].includes(char)
      )
        return;
      if (!/^[a-zA-ZçğıöşüÇĞIİÖŞÜ\s]$/.test(char)) {
        e.preventDefault();
      }
    },
    [],
  );

  return (
    <>
      <div className="grid items-start grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Ad *
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Adınız"
                  className="h-12"
                  {...field}
                  onChange={(e) => handleNameInput(e, field)}
                  onKeyDown={handleNameKeyPress}
                  autoComplete="given-name"
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700">
                Soyad *
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Soyadınız"
                  className="h-12"
                  {...field}
                  onChange={(e) => handleNameInput(e, field)}
                  onKeyDown={handleNameKeyPress}
                  autoComplete="family-name"
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="gender"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-gray-700">
              Cinsiyet *
            </FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Cinsiyetinizi seçin" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="male">Erkek</SelectItem>
                <SelectItem value="female">Kadın</SelectItem>
                <SelectItem value="unknown">Belirtmek İstemiyorum</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-gray-700">
              E-posta Adresi *
            </FormLabel>
            <FormControl>
              <Input
                type="email"
                placeholder="ornek@email.com"
                className="h-12"
                {...field}
                autoComplete="email"
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium text-gray-700">
              Telefon Numarası *
            </FormLabel>
            <FormControl>
              <PhoneInput
                className="h-12"
                value={field.value}
                onChange={field.onChange}
                onCountryChange={(countryId) => {
                  form.setValue("countryId", countryId);
                }}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export default RegisterInfoForm;